import { useState } from "react";
import { Check } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { money, imgUrl, STATUS_FLOW, STATUS_LABELS, PAYMENT_LABELS, fmtDate } from "../lib/shop";
import { Seo } from "../components/site/Seo";

export function Timeline({ order }) {
  if (order.status === "cancelled") return <p className="text-red-400 font-bold" data-testid="timeline-cancelled">This order was cancelled.</p>;
  const idx = STATUS_FLOW.indexOf(order.status);
  const at = (s) => [...order.status_history].reverse().find((h) => h.status === s)?.at;
  return (
    <ol className="relative border-l border-white/10 ml-3 space-y-6" data-testid="order-timeline">
      {STATUS_FLOW.map((s, i) => (
        <li key={s} className="pl-8 relative">
          <span className={`absolute -left-[13px] top-0 w-6 h-6 rounded-full grid place-items-center ${i <= idx ? "bg-[#d4af37] text-black" : "bg-[#18181c] border border-white/20"}`}>
            {i <= idx && <Check size={14} />}
          </span>
          <p className={`font-bold ${i <= idx ? "text-white" : "text-zinc-500"}`}>{STATUS_LABELS[s]}</p>
          {i <= idx && at(s) && <p className="text-xs text-zinc-500">{fmtDate(at(s))}</p>}
        </li>
      ))}
    </ol>
  );
}

export default function TrackOrder() {
  const [form, setForm] = useState({ order_number: "", contact: "" });
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setRes(null);
    try { setRes((await api.post("/track", form)).data); } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <div className="container-x py-12 sm:py-20 grid lg:grid-cols-2 gap-12">
      <Seo title="Track Your Order" description="Track your Mwezzy Footwear order with your order number and email or mobile number." />
      <div>
        <p className="eyebrow">Where are my kicks?</p>
        <h1 className="font-display text-6xl sm:text-8xl leading-none mt-2">Track Order</h1>
        <form onSubmit={submit} className="mt-8 space-y-4 max-w-md" data-testid="track-form">
          <div><label className="label">Order number</label><input required className="field uppercase" placeholder="MWZ-10001" value={form.order_number} onChange={(e) => setForm({ ...form, order_number: e.target.value })} data-testid="order-track-input" /></div>
          <div><label className="label">Email or mobile number</label><input required className="field" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="order-track-contact-input" /></div>
          <button disabled={busy} className="btn-gold w-full" data-testid="order-track-submit">{busy ? "Searching..." : "Track order"}</button>
          {err && <p className="text-sm text-red-400" data-testid="order-track-error">{err}</p>}
        </form>
      </div>
      {res && (
        <div className="card-dark p-6 sm:p-8" data-testid="track-result">
          <p className="font-display text-5xl text-gold-gradient">{res.order_number}</p>
          <p className="text-sm text-zinc-400 mt-1">Placed {fmtDate(res.created_at)} · {money(res.total)} · Payment: {PAYMENT_LABELS[res.payment_status]}</p>
          <p className="text-lg font-bold mt-3" data-testid="track-current-status">Current status: <span className="text-gold">{STATUS_LABELS[res.status]}</span></p>
          {res.tracking_number && <p className="text-sm mt-1">Courier tracking no: <b>{res.tracking_number}</b></p>}
          <div className="flex gap-2 mt-5 overflow-x-auto no-scrollbar">
            {res.items.map((i) => <img key={i.name + i.size} src={imgUrl(i.image)} alt={i.name} title={`${i.name} ${i.size}`} className="w-16 h-16 shrink-0 rounded-lg object-cover" />)}
          </div>
          <div className="mt-8"><Timeline order={res} /></div>
        </div>
      )}
    </div>
  );
}
